import { useEffect, useState } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Group,
  Loader,
  Modal,
  ScrollArea,
  SimpleGrid,
  Stack,
  Tabs,
  Text,
  Title,
  UnstyledButton,
  Tooltip,
  ActionIcon,
} from '@mantine/core';
import {
  IconCalendarEvent,
  IconFile,
  IconHeadphones,
  IconMusic,
  IconSettings,
  IconSpeakerphone,
  IconUsers,
  IconBellRinging,
  IconCheck,
  IconX,
  IconQuestionMark,
  IconBeach,
  IconChevronRight,
  IconMapPin,
  IconClock,
  IconRefresh,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth/useAuth';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabaseClient';
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../lib/env';
import { notifications } from '@mantine/notifications';

// ── Types ──────────────────────────────────────────────────────────────────

function formatLastLogin(dateStr: string | undefined): string {
  if (!dateStr) return '–';
  try {
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateStr));
  } catch {
    return '–';
  }
}

function getInitials(email: string | undefined): string {
  if (!email) return '?';
  const name = email.split('@')[0];
  const parts = name.split(/[._-]/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

function formatEventDate(iso: string): string {
  const dt = new Date(iso);
  return dt.toLocaleDateString('de-DE', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatEventTime(iso: string): string {
  const dt = new Date(iso);
  return dt.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

function formatEventTimeRange(start: string, end: string | null): string {
  const s = formatEventTime(start);
  if (!end) return s;
  return `${s} – ${formatEventTime(end)}`;
}

function formatLateArrivalTime(raw: string | null | undefined): string {
  const match = /^(\d{1,2}):(\d{2})/.exec((raw ?? '').trim());
  if (!match) return '';
  return `${match[1].padStart(2, '0')}:${match[2]}`;
}

type UpcomingEvent = {
  id: string;
  title: string;
  event_type: string;
  start_time: string;
  end_time: string | null;
  location_name: string | null;
  requires_attendance_check: boolean;
};

type AttendanceRow = {
  event_id: string;
  user_id: string;
  status: string;
  late_arrival_time: string | null;
  first_name: string | null;
  last_name: string | null;
  nickname: string | null;
  avatar_url: string | null;
  attendance_created_at: string | null;
};

type EventRsvpSummary = {
  present: number;
  absent: number;
  user_absence: number;
  undecided: number;
  late: number;
  total: number;
};

type EventWithAttendees = UpcomingEvent & {
  summary: EventRsvpSummary;
  attendees: AttendanceRow[];
};

// ── Quick Link Card ─────────────────────────────────────────────────────────

interface QuickLinkProps {
  label: string;
  description: string;
  icon: React.ReactNode;
  path: string;
}

function QuickLinkCard({ label, description, icon, path }: QuickLinkProps) {
  const nav = useNavigate();
  return (
    <UnstyledButton
      onClick={() => nav(path)}
      style={{ display: 'block', width: '100%' }}
    >
      <Card radius="lg" p="md" className="premium-card">
        <Group gap="sm" wrap="nowrap">
          <Box
            style={{
              background: 'linear-gradient(135deg, var(--mantine-color-brand-7) 0%, var(--mantine-color-brand-9) 100%)',
              borderRadius: 10,
              padding: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              color: 'var(--mantine-color-brand-2)',
              boxShadow: '0 2px 8px rgba(143, 135, 255, 0.20)',
            }}
          >
            {icon}
          </Box>
          <Box>
            <Text fw={700} c="var(--color-text)" size="sm">
              {label}
            </Text>
            <Text size="xs" c="dimmed" lineClamp={1}>
              {description}
            </Text>
          </Box>
        </Group>
      </Card>
    </UnstyledButton>
  );
}

// ── RSVP Chip ──────────────────────────────────────────────────────────────

function RsvpChip({ icon, count, color, label }: { icon: React.ReactNode; count: number; color: string; label: string }) {
  return (
    <Tooltip label={label} withArrow>
      <Group gap={4} style={{ cursor: 'default' }}>
        <Box style={{ color, display: 'flex', alignItems: 'center' }}>{icon}</Box>
        <Text fw={700} size="sm" c={count > 0 ? color : 'var(--color-text-muted)'}>
          {count}
        </Text>
      </Group>
    </Tooltip>
  );
}

// ── Attendee Detail Modal ──────────────────────────────────────────────────

function AttendeeDetailModal({
  event,
  opened,
  onClose,
}: {
  event: EventWithAttendees | null;
  opened: boolean;
  onClose: () => void;
}) {
  const [sending, setSending] = useState(false);

  if (!event) return null;
  // Capture non-null for use in async closures
  const ev = event;

  const groups = {
    all: event.attendees,
    present: event.attendees.filter((a) => a.status === 'present'),
    absent: event.attendees.filter((a) => a.status === 'absent'),
    user_absence: event.attendees.filter((a) => a.status === 'user_absence'),
    undecided: event.attendees.filter((a) => a.status === 'undecided'),
  };

  const statusIcon = (status: string) => {
    if (status === 'present') return <IconCheck size={14} />;
    if (status === 'absent') return <IconX size={14} />;
    if (status === 'user_absence') return <IconBeach size={14} />;
    return <IconQuestionMark size={14} />;
  };

  const statusColor = (status: string) => {
    if (status === 'present') return 'teal';
    if (status === 'absent') return 'red';
    if (status === 'user_absence') return 'orange';
    return 'gray';
  };

  const displayName = (a: AttendanceRow) => {
    const parts = [a.first_name, a.last_name].filter(Boolean).join(' ');
    return a.nickname ? `${parts} (${a.nickname})` : parts || 'Unbekannt';
  };

  async function sendReminder() {
    if (ev.summary.undecided === 0) return;
    const title = `Bitte Rückmeldung: ${formatEventDate(ev.start_time)}`;
    const body = `Hey, bitte kurz in der App Bescheid geben ob du am ${formatEventDate(ev.start_time)} ${formatEventTime(ev.start_time)} bei "${ev.title}" dabei bist. Danke!`;

    setSending(true);
    try {
      const { data: { session: s } } = await supabase.auth.getSession();
      const res = await fetch(
        `${SUPABASE_URL}/functions/v1/send-event-attendance-reminder`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${s?.access_token ?? ''}`,
            apikey: SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ event_id: ev.id, title, body }),
        },
      );
      const json = await res.json();
      if (!json.ok) throw new Error(json.error ?? 'Fehler beim Senden');
      notifications.show({
        color: 'teal',
        title: 'Erinnerung gesendet',
        message: `${json.recipients ?? 0} Empfänger · ${json.sent_users ?? 0} erreicht`,
      });
    } catch (e) {
      notifications.show({
        color: 'red',
        title: 'Fehler',
        message: (e as Error).message,
      });
    } finally {
      setSending(false);
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Box>
          <Text fw={700} size="md" c="var(--color-text)">{event.title}</Text>
          <Text size="xs" c="dimmed">
            {formatEventDate(event.start_time)} · {formatEventTimeRange(event.start_time, event.end_time)}
            {event.location_name ? ` · ${event.location_name}` : ''}
          </Text>
        </Box>
      }
      size="lg"
      styles={{
        content: { background: 'var(--color-surface)', border: '1px solid var(--app-border)' },
        header: { background: 'var(--color-surface)', borderBottom: '1px solid var(--app-border)', paddingBottom: 12 },
        title: { flex: 1 },
      }}
    >
      {/* RSVP Zusammenfassung */}
      <Group gap="xl" mb="md" p="sm"
        style={{ background: 'var(--app-surface-2)', borderRadius: 8, border: '1px solid var(--app-border)' }}
      >
        {[
          { label: 'Zugesagt', count: event.summary.present, color: 'var(--color-success)', icon: <IconCheck size={16} /> },
          { label: 'Verspätet', count: event.summary.late, color: 'var(--mantine-color-blue-5)', icon: <IconClock size={16} /> },
          { label: 'Abgesagt', count: event.summary.absent, color: 'var(--color-danger)', icon: <IconX size={16} /> },
          { label: 'Urlaub', count: event.summary.user_absence, color: 'var(--color-warning)', icon: <IconBeach size={16} /> },
          { label: 'Offen', count: event.summary.undecided, color: 'var(--color-text-muted)', icon: <IconQuestionMark size={16} /> },
        ].map(({ label, count, color, icon }) => (
          <Stack key={label} gap={2} align="center">
            <Group gap={4}>
              <Box style={{ color }}>{icon}</Box>
              <Text fw={800} size="lg" c={color}>{count}</Text>
            </Group>
            <Text size="xs" c="dimmed">{label}</Text>
          </Stack>
        ))}
        <Box style={{ flex: 1 }} />
        {event.requires_attendance_check && event.summary.undecided > 0 && (
          <Button
            size="xs"
            leftSection={<IconBellRinging size={14} />}
            color="brand"
            loading={sending}
            onClick={sendReminder}
          >
            Erinnern ({event.summary.undecided})
          </Button>
        )}
      </Group>

      {/* Tabs */}
      <Tabs defaultValue="all" className="contrast-tabs">
        <Tabs.List>
          <Tabs.Tab value="all">Alle ({groups.all.length})</Tabs.Tab>
          <Tabs.Tab value="present" c="teal">✓ {groups.present.length}</Tabs.Tab>
          <Tabs.Tab value="absent" c="red">✗ {groups.absent.length}</Tabs.Tab>
          <Tabs.Tab value="user_absence">🏖 {groups.user_absence.length}</Tabs.Tab>
          <Tabs.Tab value="undecided">? {groups.undecided.length}</Tabs.Tab>
        </Tabs.List>

        {(['all', 'present', 'absent', 'user_absence', 'undecided'] as const).map((tab) => (
          <Tabs.Panel key={tab} value={tab} pt="sm">
            <ScrollArea h={320}>
              <Stack gap={4}>
                {groups[tab].length === 0 ? (
                  <Text size="sm" c="dimmed" ta="center" py="md">Keine Einträge</Text>
                ) : (
                  groups[tab].map((a) => {
                    const lateArrival = a.status === 'present' ? formatLateArrivalTime(a.late_arrival_time) : '';
                    const statusLabel = a.status === 'present'
                      ? (lateArrival ? `Zugesagt ab ${lateArrival}` : 'Zugesagt')
                      : a.status === 'absent'
                        ? 'Abgesagt'
                        : a.status === 'user_absence'
                          ? 'Urlaub'
                          : 'Offen';

                    return (
                      <Group key={a.user_id} gap="sm" px="xs" py={6}
                        style={{
                          borderRadius: 8,
                          background: a.status === 'present'
                            ? 'color-mix(in srgb, var(--color-success) 8%, transparent)'
                            : a.status === 'absent'
                              ? 'color-mix(in srgb, var(--color-danger) 8%, transparent)'
                              : a.status === 'user_absence'
                                ? 'color-mix(in srgb, var(--color-warning) 8%, transparent)'
                                : 'var(--app-surface-2)',
                        }}
                      >
                        <Avatar src={a.avatar_url} size={32} radius="xl" color="brand">
                          {displayName(a).slice(0, 1).toUpperCase()}
                        </Avatar>
                        <Box style={{ flex: 1 }}>
                          <Text size="sm" fw={500} c="var(--color-text)">
                            {displayName(a)}
                          </Text>
                          {lateArrival && (
                            <Text size="xs" c="blue" fw={700}>
                              Kommt ab {lateArrival}
                            </Text>
                          )}
                        </Box>
                        <Badge size="xs" color={statusColor(a.status)} variant="light" leftSection={statusIcon(a.status)}>
                          {statusLabel}
                        </Badge>
                      </Group>
                    );
                  })
                )}
              </Stack>
            </ScrollArea>
          </Tabs.Panel>
        ))}
      </Tabs>
    </Modal>
  );
}

// ── Upcoming Events Section ────────────────────────────────────────────────

function UpcomingEventsSection({ canViewAttendance }: { canViewAttendance: boolean }) {
  const qc = useQueryClient();
  const [selectedEvent, setSelectedEvent] = useState<EventWithAttendees | null>(null);

  // Fetch upcoming events (next 21 days)
  const until = new Date(Date.now() + 21 * 24 * 60 * 60 * 1000).toISOString();
  const eventsQ = useQuery({
    queryKey: ['home-upcoming-events'],
    queryFn: async (): Promise<UpcomingEvent[]> => {
      const { data, error } = await supabase
        .from('events')
        .select('id,title,event_type,start_time,end_time,location_name,requires_attendance_check')
        .gte('start_time', new Date().toISOString())
        .lte('start_time', until)
        .order('start_time')
        .limit(25);
      if (error) throw error;
      return (data ?? []) as UpcomingEvent[];
    },
    staleTime: 2 * 60 * 1000,
  });

  // Fetch attendance for all upcoming events (only if permission available)
  const eventIds = (eventsQ.data ?? []).map((e) => e.id);
  const attendanceQ = useQuery({
    queryKey: ['home-attendance', eventIds],
    enabled: canViewAttendance && eventIds.length > 0,
    queryFn: async (): Promise<AttendanceRow[]> => {
      const { data, error } = await supabase.rpc('get_event_attendance_overview', {
        p_event_ids: eventIds,
      });
      if (error) throw error;
      return (data ?? []) as AttendanceRow[];
    },
    staleTime: 2 * 60 * 1000,
  });

  const eventsWithData: EventWithAttendees[] = (eventsQ.data ?? []).map((ev) => {
    const rows = canViewAttendance
      ? (attendanceQ.data ?? []).filter((a) => a.event_id === ev.id)
      : [];
    const summary: EventRsvpSummary = {
      present: rows.filter((r) => r.status === 'present').length,
      absent: rows.filter((r) => r.status === 'absent').length,
      user_absence: rows.filter((r) => r.status === 'user_absence').length,
      undecided: rows.filter((r) => r.status === 'undecided').length,
      late: rows.filter((r) => r.status === 'present' && formatLateArrivalTime(r.late_arrival_time)).length,
      total: rows.length,
    };
    return { ...ev, summary, attendees: rows };
  });

  // Group by date
  const byDate = new Map<string, EventWithAttendees[]>();
  for (const ev of eventsWithData) {
    const dateKey = new Date(ev.start_time).toLocaleDateString('de-DE', {
      weekday: 'long',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    if (!byDate.has(dateKey)) byDate.set(dateKey, []);
    byDate.get(dateKey)!.push(ev);
  }

  const isLoading = eventsQ.isLoading || (canViewAttendance && eventIds.length > 0 && attendanceQ.isLoading);

  if (eventsQ.data?.length === 0 && !isLoading) {
    return (
      <Stack gap="sm">
        <Divider
          label={<Text size="xs" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.06em' }}>Nächste Termine</Text>}
        />
        <Text size="sm" c="dimmed" py="md" ta="center">Keine Termine in den nächsten 3 Wochen.</Text>
      </Stack>
    );
  }

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Divider
          style={{ flex: 1 }}
          label={<Text size="xs" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.06em' }}>Nächste Termine</Text>}
        />
        <Tooltip label="Aktualisieren" withArrow>
          <ActionIcon
            variant="subtle"
            size="sm"
            className="contrast-icon-btn"
            loading={isLoading}
            onClick={() => {
              qc.invalidateQueries({ queryKey: ['home-upcoming-events'] });
              qc.invalidateQueries({ queryKey: ['home-attendance'] });
            }}
          >
            <IconRefresh size={14} />
          </ActionIcon>
        </Tooltip>
      </Group>

      {isLoading && (
        <Group justify="center" py="md">
          <Loader size="sm" color="brand" />
          <Text size="sm" c="dimmed">Lade Termine…</Text>
        </Group>
      )}

      {!isLoading && Array.from(byDate.entries()).map(([dateLabel, dayEvents]) => (
        <Stack key={dateLabel} gap={6}>
          {/* Tages-Trennlinie */}
          <Group gap="sm" align="center">
            <Box
              style={{
                background: 'var(--color-primary)',
                borderRadius: 4,
                padding: '2px 8px',
                flexShrink: 0,
              }}
            >
              <Text size="xs" fw={700} c="white" style={{ letterSpacing: '0.03em' }}>
                {dateLabel}
              </Text>
            </Box>
            <Box style={{ flex: 1, height: 1, background: 'var(--app-border)' }} />
          </Group>

          {/* Events an diesem Tag */}
          {dayEvents.map((ev) => (
            <UnstyledButton
              key={ev.id}
              onClick={() => canViewAttendance ? setSelectedEvent(ev) : undefined}
              style={{ display: 'block', width: '100%', cursor: canViewAttendance ? 'pointer' : 'default' }}
            >
              <Card
                radius="md"
                p="sm"
                className="premium-card"
                style={{ borderLeft: '3px solid var(--color-primary)' }}
              >
                <Group justify="space-between" wrap="nowrap" gap="sm">
                  <Box style={{ flex: 1, overflow: 'hidden' }}>
                    {/* Titel + Typ */}
                    <Group gap="xs" mb={4}>
                      <Text fw={700} size="sm" c="var(--color-text)" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {ev.title}
                      </Text>
                      <Badge size="xs" variant="light" color="brand">
                        {ev.event_type}
                      </Badge>
                    </Group>

                    {/* Zeit + Ort */}
                    <Group gap="md">
                      <Group gap={4}>
                        <IconClock size={12} color="var(--color-text-muted)" />
                        <Text size="xs" c="dimmed">
                          {formatEventTimeRange(ev.start_time, ev.end_time)}
                        </Text>
                      </Group>
                      {ev.location_name && (
                        <Group gap={4}>
                          <IconMapPin size={12} color="var(--color-text-muted)" />
                          <Text size="xs" c="dimmed" lineClamp={1}>
                            {ev.location_name}
                          </Text>
                        </Group>
                      )}
                    </Group>
                  </Box>

                  {/* RSVP Chips (nur wenn Attendance aktiv) */}
                  {canViewAttendance && ev.requires_attendance_check && ev.summary.total > 0 && (
                    <Group gap="sm" wrap="nowrap" style={{ flexShrink: 0 }}>
                      <RsvpChip icon={<IconCheck size={14} />} count={ev.summary.present} color="var(--color-success)" label="Zugesagt" />
                      {ev.summary.late > 0 && (
                        <RsvpChip icon={<IconClock size={14} />} count={ev.summary.late} color="var(--mantine-color-blue-5)" label="Verspätet" />
                      )}
                      <RsvpChip icon={<IconX size={14} />} count={ev.summary.absent} color="var(--color-danger)" label="Abgesagt" />
                      <RsvpChip icon={<IconQuestionMark size={14} />} count={ev.summary.undecided} color="var(--color-text-muted)" label="Noch offen" />
                    </Group>
                  )}

                  {canViewAttendance && (
                    <Box style={{ color: 'var(--color-text-muted)', flexShrink: 0 }}>
                      <IconChevronRight size={16} />
                    </Box>
                  )}
                </Group>
              </Card>
            </UnstyledButton>
          ))}
        </Stack>
      ))}

      {/* Detail-Modal */}
      <AttendeeDetailModal
        event={selectedEvent}
        opened={selectedEvent !== null}
        onClose={() => setSelectedEvent(null)}
      />
    </Stack>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────

export function HomePage() {
  const { session, capabilities } = useAuth();

  useEffect(() => {
    document.title = 'Übersicht – Musical!Kultur Admin';
  }, []);
  const email = session?.user?.email;
  const lastLogin = (session?.user as { last_sign_in_at?: string } | undefined)?.last_sign_in_at;

  const adminLinks: QuickLinkProps[] = [
    {
      label: 'News',
      description: 'Meldungen erstellen & Push-Nachrichten senden',
      icon: <IconSpeakerphone size={20} />,
      path: '/news',
    },
    {
      label: 'Formulare',
      description: 'Formulare erstellen, veröffentlichen & exportieren',
      icon: <IconFile size={20} />,
      path: '/forms',
    },
    {
      label: 'Chor Uploads',
      description: 'Aufnahmen ins Supabase Storage hochladen',
      icon: <IconMusic size={20} />,
      path: '/choir',
    },
    {
      label: 'Playbacks',
      description: 'MP3s über Bunny CDN bereitstellen',
      icon: <IconHeadphones size={20} />,
      path: '/playbacks',
    },
    {
      label: 'Einstellungen',
      description: 'Kalender, Songs, Rollen & Ressorts konfigurieren',
      icon: <IconSettings size={20} />,
      path: '/config',
    },
    {
      label: 'Mitglieder',
      description: 'Zuweisungen, Rollen & Admin-Rechte verwalten',
      icon: <IconUsers size={20} />,
      path: '/members',
    },
  ];

  const eventLinks: QuickLinkProps[] = [
    {
      label: 'Kalender',
      description: 'Termine anlegen, duplizieren & importieren',
      icon: <IconCalendarEvent size={20} />,
      path: '/calendar',
    },
  ];

  const roleLabel = capabilities.isAdmin
    ? 'Admin'
    : capabilities.canManageEvents
      ? 'Events verwalten'
      : 'Read-only';

  const roleBadgeColor = capabilities.isAdmin
    ? 'brand'
    : capabilities.canManageEvents
      ? 'teal'
      : 'gray';

  const canViewAttendance = capabilities.canManageEvents || capabilities.isAdmin;

  return (
    <Stack gap="xl" className="contrast-page">
      {/* Begrüßungs-Header */}
      <Card
        radius="lg"
        p="lg"
        className="premium-card"
        style={{ cursor: 'default', borderLeft: '4px solid var(--color-primary)' }}
      >
        <Group justify="space-between" align="center" wrap="wrap" gap="md">
          <Group gap="md">
            <Avatar
              size={52}
              radius="xl"
              color="brand"
              variant="filled"
              style={{ fontWeight: 700, fontSize: 18 }}
            >
              {getInitials(email)}
            </Avatar>
            <Box>
              <Title order={3} c="var(--color-text)">
                Willkommen zurück
              </Title>
              <Text size="sm" c="dimmed">
                {email ?? '–'}
              </Text>
            </Box>
          </Group>
          <Stack gap={4} align="flex-end">
            <Badge color={roleBadgeColor} variant="light" size="md">
              {roleLabel}
            </Badge>
            <Text size="xs" c="dimmed">
              Letzter Login: {formatLastLogin(lastLogin)}
            </Text>
          </Stack>
        </Group>
      </Card>

      {/* Nächste Termine — nur für Event-Manager/Admin */}
      {canViewAttendance && (
        <UpcomingEventsSection canViewAttendance={canViewAttendance} />
      )}

      {/* Schnellzugriff */}
      {(capabilities.canManageEvents || capabilities.isAdmin) && (
        <Stack gap="sm">
          <Divider
            label={
              <Text size="xs" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.06em' }}>
                Schnellzugriff
              </Text>
            }
          />
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="sm">
            {capabilities.canManageEvents && eventLinks.map((link) => (
              <QuickLinkCard key={link.path} {...link} />
            ))}
            {capabilities.isAdmin && adminLinks.map((link) => (
              <QuickLinkCard key={link.path} {...link} />
            ))}
          </SimpleGrid>
        </Stack>
      )}

      {/* Kein Zugriff */}
      {!capabilities.canManageEvents && !capabilities.isAdmin && (
        <Card radius="lg" p="lg" className="premium-card">
          <Text c="dimmed" size="sm">
            Dein Account hat aktuell nur Lesezugriff. Bitte einen Admin kontaktieren, falls du
            Schreibzugriff benötigst.
          </Text>
        </Card>
      )}
    </Stack>
  );
}
